import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, Subject } from 'rxjs';
import { ProformsAdminService } from 'src/app/private/shared/services/proforms/proforms-admin.service';
import {
  CompetencyContentUpdate,
  ProFormsQuestionAPIResponse,
  RubricFieldTypeCompetency,
  RubricFieldTypeSpecs,
} from 'src/app/private/shared/types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-admin-question-answer-rubric',
  standalone: false,
  templateUrl: './proforms-admin-question-answer-rubric.component.html',
  styleUrl: './proforms-admin-question-answer-rubric.component.scss',
})
export class ProformsAdminQuestionAnswerRubricComponent
  implements OnInit, OnChanges
{
  @Input() question: ProFormsQuestionAPIResponse;

  fieldTypeSpecs: RubricFieldTypeSpecs;

  levelsChange$ = new Subject<number>();

  levelsMax = 5;

  levels: number[];

  canDeleteCompetency = false;

  loading = true;

  constructor(private proformsAdminService: ProformsAdminService) {}

  ngOnInit() {
    this.levels = Array.from({ length: this.levelsMax }, (_, i) => i + 1);

    this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);

    this.canComptenciesBeDeleted();

    this.levelsChange$.pipe(debounceTime(300)).subscribe((value) => {
      this.updateNumbLevelsValue(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['question'] &&
      changes['question'].currentValue !== changes['question'].previousValue
    ) {
      this.loading = false;
      this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);
      this.canComptenciesBeDeleted();
    }
  }

  onLevelsValueChange(value: number) {
    this.levelsChange$.next(value);
  }

  canComptenciesBeDeleted() {
    if (this.fieldTypeSpecs) {
      if (this.fieldTypeSpecs.competencies.length > 1) {
        this.canDeleteCompetency = true;
      } else {
        this.canDeleteCompetency = false;
      }
    } else {
      this.canDeleteCompetency = false;
    }
  }

  updateNumbLevelsValue(newValue: number) {
    const currentLevel = this.fieldTypeSpecs.numbLevels;
    if (newValue > currentLevel) {
      const levelDifference = newValue - currentLevel;
      this.fieldTypeSpecs.numbLevels = newValue;
      this.fieldTypeSpecs.competencies.forEach((competency) => {
        for (let step = 0; step < levelDifference; step += 1) {
          competency.gis.push('');
        }
      });
      for (let step = 0; step < levelDifference; step += 1) {
        this.fieldTypeSpecs.levelNames.push('New Level');
      }
      this.updateFieldTypeSpecs();
    } else if (newValue < currentLevel) {
      const levelDifference = currentLevel - newValue;
      this.fieldTypeSpecs.numbLevels = newValue;
      this.fieldTypeSpecs.competencies.forEach((competency) => {
        competency.gis.splice(-levelDifference);
      });
      this.fieldTypeSpecs.levelNames = this.fieldTypeSpecs.levelNames.splice(
        0,
        this.fieldTypeSpecs.levelNames.length - levelDifference
      );
      this.updateFieldTypeSpecs();
    }
  }

  handleLevelNamesEvent(levelNames: string[]) {
    this.fieldTypeSpecs.levelNames = levelNames;
    this.updateFieldTypeSpecs();
  }

  handleCompetencyContentEvent(giData: CompetencyContentUpdate) {
    this.fieldTypeSpecs.competencies[giData.index].gis = giData.gis;
    this.fieldTypeSpecs.competencies[giData.index].title = giData.title;
    this.updateFieldTypeSpecs();
  }

  // bulk change of data
  updateFieldTypeSpecs() {
    this.loading = true;
    const newValue = JSON.stringify(this.fieldTypeSpecs);
    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      { field_type_specs: newValue }
    );
  }

  addCompetency() {
    const newCompetency: RubricFieldTypeCompetency = {
      title: 'Competency',
      gis: [],
    };
    for (let step = 0; step < this.fieldTypeSpecs.numbLevels; step += 1) {
      newCompetency.gis.push('');
    }
    this.fieldTypeSpecs.competencies.push(newCompetency);
    this.updateFieldTypeSpecs();
  }

  deleteCompetency(competency: RubricFieldTypeCompetency) {
    this.fieldTypeSpecs.competencies = this.fieldTypeSpecs.competencies.filter(
      (c) => c !== competency
    );
    this.updateFieldTypeSpecs();
  }
}
